import { createContext, useContext, useRef, useState, useEffect, useCallback, type ReactNode } from "react";
import { animate, motion, useMotionValue } from "framer-motion";
import { useGameStore } from "../store/useGameStore.js";
import { useCardSet } from "./CardSetContext.js";
import { pendingFlipFaceUrls } from "../lib/dragState.js";
import { GAME_CONFIG } from "../config.js";

// ============================================================
// FlyAnimationContext — manuelle Karten-Fluganimation
// Nutzt dieselbe Technik wie der Drag-Flyback:
// Framer Motion animate() auf MotionValues mit Spring-Physik
// ============================================================

interface FlyEntry {
  cardId: string;
  fromRect: DOMRect;
  toRect: DOMRect;
  imageUrl: string;
  /** Wenn gesetzt: Karte dreht sich erst um (3D Flip), dann fliegt sie. */
  flipToImageUrl?: string;
}

interface FlyAnimationContextValue {
  isFlying: (cardId: string) => boolean;
}

const FlyAnimationContext = createContext<FlyAnimationContextValue>({
  isFlying: () => false,
});

export function useFlyAnimation() {
  return useContext(FlyAnimationContext);
}

// Spring-Konfigurationen aus GAME_CONFIG lesen (pro Speed-Typ)
const FLY_SPEEDS: Record<string, Record<string, { stiffness: number; damping: number }>> = {
  default: GAME_CONFIG.flySpeed,
  stock:   GAME_CONFIG.stockFlySpeed,
  victory: GAME_CONFIG.victoryFlySpeed,
};

// ============================================================
// FlyingCard — animierter Karten-Klon mit MotionValues + Spring
// ============================================================

function FlyingCard({
  entry,
  spring,
  onDone,
}: {
  entry: FlyEntry;
  spring: { type: "spring"; stiffness: number; damping: number };
  onDone: () => void;
}) {
  const x = useMotionValue(entry.fromRect.left);
  const y = useMotionValue(entry.fromRect.top);
  const rotateY = useMotionValue(0);

  useEffect(() => {
    let active = true;

    async function run() {
      // Phase 1: Flip (falls flipToImageUrl gesetzt)
      if (entry.flipToImageUrl) {
        await animate(rotateY, 180, { duration: 0.3, ease: "easeInOut" });
        if (!active) return;
      }

      // Phase 2: Fly (Spring-Animation zum Ziel)
      const c1 = animate(x, entry.toRect.left, spring);
      const c2 = animate(y, entry.toRect.top, spring);
      await Promise.all([c1, c2]);
      if (active) onDone();
    }

    run();

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imgStyle = { width: "100%", height: "100%", objectFit: "fill" as const, borderRadius: "0.375rem", position: "absolute" as const, inset: 0, backfaceVisibility: "hidden" as const };

  return (
    <motion.div
      style={{
        position: "fixed",
        left: x,
        top: y,
        width: entry.fromRect.width,
        height: entry.fromRect.height,
        zIndex: 1000,
        pointerEvents: "none",
        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))",
        perspective: 800,
      }}
    >
      <motion.div style={{ width: "100%", height: "100%", rotateY, transformStyle: "preserve-3d", position: "relative" }}>
        {/* Rückseite (sichtbar bei rotateY 0–90) */}
        <img src={entry.imageUrl} alt="" style={imgStyle} draggable={false} />
        {/* Vorderseite (sichtbar bei rotateY 90–180, um 180° gegen-rotiert) */}
        {entry.flipToImageUrl && (
          <img src={entry.flipToImageUrl} alt="" style={{ ...imgStyle, transform: "rotateY(180deg)" }} draggable={false} />
        )}
      </motion.div>
    </motion.div>
  );
}

// ============================================================
// FlyOverlay — rendert alle fliegenden Karten-Klone
// ============================================================

function FlyOverlay({
  entries,
  spring,
  onComplete,
}: {
  entries: FlyEntry[];
  spring: { type: "spring"; stiffness: number; damping: number };
  onComplete: () => void;
}) {
  const completedRef = useRef(0);

  function handleOneDone() {
    completedRef.current++;
    if (completedRef.current >= entries.length) {
      onComplete();
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1000 }}>
      {entries.map((entry) => (
        <FlyingCard key={entry.cardId} entry={entry} spring={spring} onDone={handleOneDone} />
      ))}
    </div>
  );
}

// ============================================================
// FlyAnimationProvider — verwaltet den Animations-Lifecycle
// ============================================================

export function FlyAnimationProvider({ children }: { children: ReactNode }) {
  const { animationSpeed } = useCardSet();

  /** Berechnet die Spring-Config für einen Speed-Typ + User-Geschwindigkeit. */
  function getSpring(speedType: string = "default") {
    const speeds = FLY_SPEEDS[speedType] ?? FLY_SPEEDS.default!;
    const cfg = speeds[animationSpeed] ?? speeds.normal!;
    return { type: "spring" as const, ...cfg };
  }

  // Pending: vor State-Update erfasste Positionen
  const pendingRef = useRef<{
    cardIds: string[];
    sourceRects: Map<string, DOMRect>;
    sourceImageUrls: Map<string, string>;
    flipFaceUrls: Map<string, string>;
    batches?: string[][];
    speedType?: string;
  } | null>(null);

  // Aktuell fliegende Karten + deren Speed-Typ
  const [flying, setFlying] = useState<FlyEntry[] | null>(null);
  const activeSpeedTypeRef = useRef<string>("default");
  // Ref für synchronen Zugriff (onBeforeMove markiert IDs bevor React rendert)
  const flyingIdsRef = useRef<Set<string>>(new Set());
  // State-basierte Version als Re-Render-Trigger
  const [flyingIds, setFlyingIds] = useState<Set<string>>(new Set());

  // Warteschlange für sequentielle Batches
  const pendingBatchesRef = useRef<FlyEntry[][] | null>(null);
  // Generation Counter: jedes onBeforeMove inkrementiert, onAfterMove-rAF prüft
  const generationRef = useRef(0);

  const isFlying = useCallback((cardId: string): boolean => {
    return flyingIdsRef.current.has(cardId) || flyingIds.has(cardId);
  }, [flyingIds]);

  // Safety-Timeout Ref — verhindert permanent unsichtbare Karten
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Callbacks beim Store registrieren
  useEffect(() => {
    function onBeforeMove(cardIds: string[], options?: { batches?: string[][]; speedType?: string }) {
      // Generation inkrementieren — veraltete rAF-Callbacks werden ignoriert
      generationRef.current++;

      // Laufende Animation sofort beenden
      if (flyingIdsRef.current.size > 0) {
        flyingIdsRef.current.clear();
        setFlyingIds(new Set());
        setFlying(null);
        pendingBatchesRef.current = null;
      }
      // Alten Safety-Timer löschen
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }

      const sourceRects = new Map<string, DOMRect>();
      const sourceImageUrls = new Map<string, string>();

      for (const cardId of cardIds) {
        const el = document.querySelector(`[data-card-id="${cardId}"]`);
        if (el) {
          sourceRects.set(cardId, el.getBoundingClientRect());
          const img = el.querySelector("img");
          if (img) sourceImageUrls.set(cardId, img.src);
        }
      }

      // Fallback: Karten ohne DOM-Element bekommen das Rect/Image der ersten
      // gefundenen Karte (z.B. Stock-Karten die alle am gleichen Ort liegen)
      if (sourceRects.size > 0 && sourceRects.size < cardIds.length) {
        const fallbackRect = sourceRects.values().next().value!;
        const fallbackUrl = sourceImageUrls.values().next().value ?? "";
        for (const cardId of cardIds) {
          if (!sourceRects.has(cardId)) sourceRects.set(cardId, fallbackRect);
          if (!sourceImageUrls.has(cardId)) sourceImageUrls.set(cardId, fallbackUrl);
        }
      }

      // Flip-URLs aus der Shared Map lesen und konsumieren
      const flipFaceUrls = new Map<string, string>();
      for (const cardId of cardIds) {
        const url = pendingFlipFaceUrls.get(cardId);
        if (url) {
          flipFaceUrls.set(cardId, url);
          pendingFlipFaceUrls.delete(cardId);
        }
      }

      // Card-IDs sofort als "fliegend" markieren — verhindert 1-Frame-Flash
      const idSet = new Set(cardIds);
      flyingIdsRef.current = idSet;
      setFlyingIds(idSet);
      pendingRef.current = { cardIds, sourceRects, sourceImageUrls, flipFaceUrls, batches: options?.batches, speedType: options?.speedType };

      // Safety-Timeout: nach 4s erzwungen aufräumen (länger wegen Batch-Sequenzen)
      safetyTimerRef.current = setTimeout(() => {
        if (flyingIdsRef.current.size > 0) {
          console.warn("[FlyAnimation] Safety-Timeout: flyingIds erzwungen aufgeräumt", [...flyingIdsRef.current]);
          flyingIdsRef.current.clear();
          setFlyingIds(new Set());
          setFlying(null);
          pendingBatchesRef.current = null;
        }
        safetyTimerRef.current = null;
      }, 4000);
    }

    function onAfterMove() {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      const gen = generationRef.current;

      // Double-rAF: sicherstellen dass React den DOM-Commit abgeschlossen hat
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Stale Check: wenn zwischen onAfterMove und rAF ein neues onBeforeMove lief,
          // ist dieser Callback veraltet → überspringen
          if (gen !== generationRef.current) return;
          const entries: FlyEntry[] = [];
          const animatedIds = new Set<string>();

          for (const cardId of pending.cardIds) {
            const sourceRect = pending.sourceRects.get(cardId);
            const imageUrl = pending.sourceImageUrls.get(cardId);
            if (!sourceRect || !imageUrl) continue;

            const targetEl = document.querySelector(`[data-card-id="${cardId}"]`);
            if (!targetEl) continue;
            const toRect = targetEl.getBoundingClientRect();

            // Nur animieren wenn sich die Position tatsächlich geändert hat
            const dx = Math.abs(sourceRect.left - toRect.left);
            const dy = Math.abs(sourceRect.top - toRect.top);
            if (dx < 2 && dy < 2) continue;

            const flipToImageUrl = pending.flipFaceUrls.get(cardId);
            entries.push({ cardId, fromRect: sourceRect, toRect, imageUrl, flipToImageUrl });
            animatedIds.add(cardId);
          }

          if (entries.length > 0) {
            // Speed-Typ für diese Animation merken
            activeSpeedTypeRef.current = pending.speedType ?? "default";
            console.log(`[FlyAnimation] speedType="${activeSpeedTypeRef.current}", entries=${entries.length}`);

            // Sequentielle Batches?
            if (pending.batches && pending.batches.length > 1) {
              const batchGroups: FlyEntry[][] = [];
              for (const batchIds of pending.batches) {
                const batchIdSet = new Set(batchIds);
                const batchEntries = entries.filter((e) => batchIdSet.has(e.cardId));
                if (batchEntries.length > 0) batchGroups.push(batchEntries);
              }

              if (batchGroups.length > 1) {
                // Batch 0 starten, Rest in Warteschlange
                pendingBatchesRef.current = batchGroups.slice(1);
                flyingIdsRef.current = animatedIds;
                setFlyingIds(animatedIds);
                setFlying(batchGroups[0]!);
                return;
              }
            }

            // Kein Batching oder nur 1 effektiver Batch → alles zusammen animieren
            pendingBatchesRef.current = null;
            flyingIdsRef.current = animatedIds;
            setFlyingIds(animatedIds);
            setFlying(entries);
          } else {
            flyingIdsRef.current.clear();
            setFlyingIds(new Set());
          }
        });
      });
    }

    useGameStore.getState().setFlyCallbacks({ onBeforeMove, onAfterMove });
    return () => useGameStore.getState().setFlyCallbacks(null);
  }, []);

  function handleAnimationComplete() {
    const remaining = pendingBatchesRef.current;
    if (remaining && remaining.length > 0) {
      // Nächsten Batch starten
      const nextBatch = remaining.shift()!;
      if (remaining.length === 0) pendingBatchesRef.current = null;

      // FlyingIds aktualisieren: nur noch die verbleibenden Karten
      const stillFlyingIds = new Set<string>();
      for (const entry of nextBatch) stillFlyingIds.add(entry.cardId);
      if (remaining) {
        for (const batch of remaining) {
          for (const entry of batch) stillFlyingIds.add(entry.cardId);
        }
      }
      flyingIdsRef.current = stillFlyingIds;
      setFlyingIds(stillFlyingIds);
      setFlying(nextBatch);
    } else {
      // Alle Batches fertig → aufräumen
      flyingIdsRef.current.clear();
      setFlyingIds(new Set());
      setFlying(null);
      pendingBatchesRef.current = null;
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    }
  }

  return (
    <FlyAnimationContext.Provider value={{ isFlying }}>
      {children}
      {flying && flying.length > 0 && (() => {
        const s = getSpring(activeSpeedTypeRef.current);
        console.log(`[FlyAnimation] RENDER spring: type=${activeSpeedTypeRef.current} stiffness=${s.stiffness} damping=${s.damping}`);
        return <FlyOverlay entries={flying} spring={s} onComplete={handleAnimationComplete} />;
      })()}
    </FlyAnimationContext.Provider>
  );
}
